import axiosInstance from "../scenes/utils/axiosInstance";
import { API_ENDPOINTS } from "../config/api";
import { withExportLang } from "../utils/getExportLangParam";
import { getDoctorSessionCacheKey } from "../utils/authSession";

const catalogCache = new Map();
const inFlightCatalogRequests = new Map();
const patientListCache = new Map();
const inFlightPatientRequests = new Map();
const CACHE_TTL_MS = 3 * 60 * 1000;
const PATIENT_LIST_CACHE_TTL_MS = 60 * 1000; // 1 dakika

export function clearCatalogCache() {
  catalogCache.clear();
  inFlightCatalogRequests.clear();
}

export function clearPatientListCache() {
  patientListCache.clear();
  inFlightPatientRequests.clear();
}

export default class PatientService {
  getPusulaLabFilterOptions = async ({ signal } = {}) => {
    const response = await axiosInstance.get(API_ENDPOINTS.PATIENTS.PUSULA_LAB_FILTER_OPTIONS, { signal });
    return response.data;
  };

  addPatient = async (patientData) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.PATIENTS.ADD, patientData);
      clearCatalogCache();
      clearPatientListCache();
      return response.data;
    } catch (error) {
      console.log("Hasta ekleme sırasında bir hata oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  getAllPatients = async (options = {}) => {
    try {
      const _uid = getDoctorSessionCacheKey();
      const cacheKey = JSON.stringify({ _uid, ...options });
      const now = Date.now();
      const cached = patientListCache.get(cacheKey);
      if (cached && now - cached.timestamp < PATIENT_LIST_CACHE_TTL_MS) {
        return cached.data;
      }
      if (inFlightPatientRequests.has(cacheKey)) {
        return await inFlightPatientRequests.get(cacheKey);
      }

      const fetchPromise = (async () => {
        try {
          const response = await axiosInstance.get(API_ENDPOINTS.PATIENTS.ALL, options);
          const result = options.withMeta
            ? { data: response.data, headers: response.headers }
            : response.data;
          patientListCache.set(cacheKey, { data: result, timestamp: Date.now() });
          return result;
        } finally {
          inFlightPatientRequests.delete(cacheKey);
        }
      })();

      inFlightPatientRequests.set(cacheKey, fetchPromise);
      return await fetchPromise;
    } catch (error) {
      console.log("Tüm hasta bilgilerni getirirken bir hata oluştu.");
      throw error;
    }
  };

  getCachedPatients = (options = {}) => {
    const _uid = getDoctorSessionCacheKey();
    const cacheKey = JSON.stringify({ _uid, ...options });
    const cached = patientListCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < PATIENT_LIST_CACHE_TTL_MS) {
      return cached.data;
    }
    return null;
  };

  getSuggestions = async (q, limit = 20) => {
    const response = await axiosInstance.get(API_ENDPOINTS.PATIENTS.SUGGESTIONS, { params: { q, limit } });
    return response.data;
  };

  exportPatientsExcel = async (filters = {}) => {
    const response = await axiosInstance.get(API_ENDPOINTS.PATIENTS.EXPORT_EXCEL, {
      params: withExportLang(filters),
      responseType: "blob",
    });
    return response;
  };

  getExportSchema = async (params = {}) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.PATIENTS.EXPORT_SCHEMA, { params });
      return response.data;
    } catch (error) {
      console.error("Export schema fetch failed:", error);
      throw error;
    }
  };

  exportAdvancedExcel = async (payload) => {
    const response = await axiosInstance.post(API_ENDPOINTS.PATIENTS.EXPORT_ADVANCED_EXCEL, payload, {
      params: withExportLang({}),
    });
    return response;
  };

  getExportJobs = async () => {
    const response = await axiosInstance.get(API_ENDPOINTS.EXPORT_JOBS.BASE);
    return response.data;
  };

  getExportJob = async (jobId) => {
    const response = await axiosInstance.get(API_ENDPOINTS.EXPORT_JOBS.BY_ID(jobId));
    return response.data;
  };

  /**
   * Dinamik alan kataloğu (hastalar listesi filtreleri vb.).
   * @param {{ mergeAllPages?: boolean, pageSize?: number }} options
   */
  getDynamicFieldCatalog = async (options = {}) => {
    const _uid = getDoctorSessionCacheKey();
    const cacheKey = JSON.stringify({ _uid, ...options });
    const now = Date.now();
    const cached = catalogCache.get(cacheKey);
    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }
    if (inFlightCatalogRequests.has(cacheKey)) {
      return await inFlightCatalogRequests.get(cacheKey);
    }

    const fetchPromise = (async () => {
      try {
        const mergeAllPages = options.mergeAllPages !== false;
        const pageSize = Math.min(Math.max(Number(options.pageSize) || (mergeAllPages ? 200 : 50), 1), 500);
        const params = {};
        if (options.catalogOnly) {
          params.catalogOnly = "true";
        }
        if (options.filterSource != null && String(options.filterSource).trim() !== "") {
          params.filterSource = String(options.filterSource).trim();
        }
        const fetchPage = (pageIndex) =>
          axiosInstance.get(API_ENDPOINTS.PATIENTS.DYNAMIC_FIELD_CATALOG, {
            params,
            headers: {
              "x-bone-pageindex": String(pageIndex),
              "x-bone-limit": String(pageSize),
            },
          });

        const first = await fetchPage(0);
        const body = first.data || {};
        let fields = [...(body.fields || [])];
        const total = typeof body.total === "number" ? body.total : fields.length;

        if (!mergeAllPages || !body.paginated || fields.length >= total || fields.length === 0) {
          const res = { ...body, fields, total };
          catalogCache.set(cacheKey, { data: res, timestamp: Date.now() });
          return res;
        }

        let pageIndex = 1;
        const maxPages = 500;
        while (fields.length < total && pageIndex < maxPages) {
          const r = await fetchPage(pageIndex);
          const chunk = r.data?.fields || [];
          fields = fields.concat(chunk);
          if (chunk.length === 0) break;
          pageIndex += 1;
        }

        const res = { ...body, fields, total };
        catalogCache.set(cacheKey, { data: res, timestamp: Date.now() });
        return res;
      } finally {
        inFlightCatalogRequests.delete(cacheKey);
      }
    })();

    inFlightCatalogRequests.set(cacheKey, fetchPromise);
    return await fetchPromise;
  };
}
